import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from '@/i18n'
import { getCounterparty, patchCounterparty } from '@/lib/api'
import { formatDateTime, formatMoney } from '@/lib/format'

const LEVEL_KEY: Record<string, string> = {
  low: 'risk.low',
  medium: 'risk.medium',
  high: 'risk.high',
  critical: 'risk.critical',
}

function riskLabel(level: string | null | undefined): string {
  const key = LEVEL_KEY[level ?? 'low']
  return key ? t(key) : (level ?? '')
}

export function CounterpartyProfile() {
  const { counterpartyId = '' } = useParams()
  const queryClient = useQueryClient()
  const profile = useQuery({
    queryKey: ['counterparties', counterpartyId],
    queryFn: () => getCounterparty(counterpartyId),
    enabled: Boolean(counterpartyId),
  })
  const watch = useMutation({
    mutationFn: (value: boolean) => patchCounterparty(counterpartyId, { is_watchlisted: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['counterparties'] })
    },
  })

  const row = profile.data
  return (
    <div className="mx-auto max-w-5xl px-4 py-5 md:px-8 md:py-8">
      <Link to="/counterparties" className="text-sm text-slate-400 hover:text-white">
        ← {t('cpp.back')}
      </Link>
      {profile.isLoading && <p className="mt-6 text-sm text-slate-500">{t('common.loading')}</p>}
      {profile.isError && (
        <p className="mt-6 text-sm text-loss">{profile.error instanceof Error ? profile.error.message : t('common.error')}</p>
      )}
      {row && (
        <>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-slate-100">{row.nickname ?? row.display_label ?? t('cpp.one')}</h1>
              <p className="mt-1 text-sm text-slate-500">
                {t('cpp.meta', { ex: row.exchange_code, n: row.orders_total, vol: formatMoney(row.volume_base) })}
              </p>
            </div>
            <button
              type="button"
              className="rounded-md border border-surface-border px-3 py-1.5 text-sm text-slate-300"
              onClick={() => watch.mutate(!(row.is_watchlisted ?? false))}
            >
              {row.is_watchlisted ? t('cpp.unwatch') : t('cpp.watch')}
            </button>
          </div>

          <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
            <h2 className="text-sm font-medium text-slate-200">{t('cpp.risk')}</h2>
            <p className="mt-1 text-sm text-slate-400">
              {t('cpp.riskLead', { level: riskLabel(row.risk_level), score: row.risk_score ?? '0' })}
            </p>
            <ul className="mt-3 space-y-1 text-sm text-slate-400">
              {(row.risk_factors ?? []).length === 0 && <li>{t('cpp.noFactors')}</li>}
              {(row.risk_factors ?? []).map((item) => (
                <li key={item.code}>
                  <span className="text-slate-200">{item.label}</span>
                  <span className="num text-slate-500">{t('cpp.weight', { n: item.weight })}</span>
                  {item.detail ? ` — ${item.detail}` : ''}
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-6">
            <h2 className="text-sm font-medium text-slate-200">{t('cpp.history')}</h2>
            <table className="mt-3 w-full text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="py-1">{t('cpp.when')}</th>
                  <th className="py-1">{t('cpp.side')}</th>
                  <th className="py-1 text-right">{t('tx.amount')}</th>
                  <th className="py-1">{t('tx.recon')}</th>
                </tr>
              </thead>
              <tbody>
                {row.recent_orders.map((order) => (
                  <tr key={order.id} className="border-t border-surface-border/60">
                    <td className="py-2 text-slate-400">{formatDateTime(order.occurred_at)}</td>
                    <td className="py-2">{order.side === 'sell' ? t('common.sell') : t('common.buy')}</td>
                    <td className="num py-2 text-right">{formatMoney(order.fiat_amount, order.fiat_code)}</td>
                    <td className="py-2 text-slate-400">{order.recon_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="mt-6">
            <h2 className="text-sm font-medium text-slate-200">{t('cpp.events')}</h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-400">
              {row.events.length === 0 && <li>{t('cpp.noEvents')}</li>}
              {row.events.map((event) => (
                <li key={event.id} className="rounded-md border border-surface-border px-3 py-2">
                  <div className="text-slate-200">{event.title}</div>
                  <div className="text-xs text-slate-500">
                    {formatDateTime(event.detected_at)} · {event.status} · {t('al.score', { n: event.score ?? '—' })}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  )
}
